import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../api/axiosInstance";

type AdminStateApi = {
    roles: {
        list: Array<any>,
        count: number
    },
    users: {
        list: Array<any>,
        count: number
    },
    vendors: {
        list: Array<any>,
        count: number
    },
    suppliers: {
        list: Array<any>,
        count: number
    },
    customers: {
        list: Array<any>,
        count: number
    },
    processList: {
        list: Array<any>,
        count: number
    },
    enquiries: {
        list: Array<any>,
        count: number
    },
    status: 'loading' | 'idle' | 'error',
    error: string | null,
    message: string | null
}

const initialState: AdminStateApi = {
    roles: {
        list: [],
        count: 0
    },
    users: {
        list: [],
        count: 0
    },
    vendors: {
        list: [],
        count: 0
    },
    suppliers: {
        list: [],
        count: 0
    },
    customers: {
        list: [],
        count: 0
    },
    processList: {
        list: [],
        count: 0
    },
    enquiries: {
        list: [],
        count: 0
    },
    status: 'idle',
    error: null,
    message: null
}

export const fetchRoles = createAsyncThunk('roles', async (data?: { searchText?: String, limit?: number, page?: number }) => {
    const response = await axiosInstance.get('admin/roles',
        {
            params: {
                search: data?.searchText,
                limit: data?.limit,
                page: data?.page
            },
            headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
        })
    const resData = response.data
    return resData
})

export const createNewRole = createAsyncThunk('createRole', async (data: any) => {
    const response = await axiosInstance.post('admin/createRole', data, {
        headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
    })
    const resData = response.data.message
    return resData
})

export const updateRole = createAsyncThunk('updateRole', async (data: any) => {
    const response = await axiosInstance.put('admin/updateRole', data, {
        headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
    })
    const resData = response.data.message
    return resData
})

export const deleteRole = createAsyncThunk('deleteRole', async (data: any) => {
    const response = await axiosInstance.post('admin/deleteRole', data, {
        headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
    })
    const resData = response.data.message
    return resData
})

export const fetchUsers = createAsyncThunk('users', async (data?: { searchText?: String, limit?: number, page?: number }) => {
    const response = await axiosInstance.get('admin/users',
        {
            params: {
                search: data?.searchText,
                limit: data?.limit,
                page: data?.page
            },
            headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
        })
    const resData = response.data
    return resData
})

export const createNewUser = createAsyncThunk('createUser', async (data: any) => {
    const response = await axiosInstance.post('admin/createUser', data, {
        headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
    })
    const resData = response.data.message
    return resData
})

export const updateUser = createAsyncThunk('updateUser', async (data: any) => {
    const response = await axiosInstance.post('admin/updateUser', data, {
        headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
    })
    const resData = response.data.message
    return resData
})

export const deleteUser = createAsyncThunk('deleteUser', async (data: any) => {
    const response = await axiosInstance.post('admin/deleteUser', data, {
        headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
    })
    const resData = response.data.message
    return resData
})

export const fetchVendors = createAsyncThunk('vendors', async (data: { searchText?: String, limit?: number, page?: number, type?:string } | undefined, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get('admin/vendorsList',
            {
                params: {
                    search: data?.searchText,
                    limit: data?.limit,
                    page: data?.page,
                    type: data?.type
                },
                headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
            })
        const resData = response.data
        return resData
    } catch (err: any) {
        return 'error'
    }
})

export const fetchVendorDetail = createAsyncThunk("fetchVendorDetail", async (data:any) => {
    const response = await axiosInstance.get(`admin/vendor/${data}`, {
        headers: { 'Authorization': `Bearer ${JSON.parse(localStorage.getItem('userDetail') as string).accessToken}`}
    })
    const resData = response.data
    return resData
})

export const createVendor = createAsyncThunk('createVendor', async (data: any) => {
    const response = await axiosInstance.post('admin/createVendor', data, {
        headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
    })
    const resData = response.data.message
    return resData
})

export const updateVendor = createAsyncThunk('updateVendor', async (data: any) => {
    const response = await axiosInstance.post('admin/updateVendor', data, {
        headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
    })
    const resData = response.data.message
    return resData
})

export const deleteVendor = createAsyncThunk('deleteVendor', async (data: any) => {
    const response = await axiosInstance.post('admin/deleteVendor', data, {
        headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
    })
    const resData = response.data.message
    return resData
})

export const makeVendorPayment = createAsyncThunk('vendorPayment', async (data: any) => {
    const response = await axiosInstance.post('admin/vendorPayment', data, {
        headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
    })
    const resData = response.data
    return resData
})

export const fetchSuppliers = createAsyncThunk('suppliers', async (data?: { searchText?: String, limit?: number, page?: number }) => {
    try {
        const response = await axiosInstance.get('admin/suppliersList',
            {
                params: {
                    search: data?.searchText,
                    limit: data?.limit,
                    page: data?.page
                },
                headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
            })
        const resData = response.data
        return resData
    } catch (err: any) {
        throw new Error(err.message)
    }
})

export const fetchSupplierDetail = createAsyncThunk("fetchSupplierDetail", async (data:any) => {
    const response = await axiosInstance.get(`admin/supplier/${data}`, {
        headers: { 'Authorization': `Bearer ${JSON.parse(localStorage.getItem('userDetail') as string).accessToken}`}
    })
    const resData = response.data
    return resData
})

export const createSupplier = createAsyncThunk('createSupplier', async (data: any) => {
    const response = await axiosInstance.post('admin/createSupplier', data, {
        headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
    })
    const resData = response.data.message
    return resData
})

export const updateSupplier = createAsyncThunk('updateSupplier', async (data: any) => {
    const response = await axiosInstance.post('admin/updateSupplier', data, {
        headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
    })
    const resData = response.data.message
    return resData
})

export const deleteSupplier = createAsyncThunk('deleteSupplier', async (data: any) => {
    const response = await axiosInstance.post('admin/deleteSupplier', data, {
        headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
    })
    const resData = response.data.message
    return resData
})

export const fetchCustomers = createAsyncThunk('customers', async (data?: { searchText?: String, limit?: number, page?: number }) => {
    const response = await axiosInstance.get('admin/customersList',
        {
            params: {
                search: data?.searchText,
                limit: data?.limit,
                page: data?.page
            },
            headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
        })
    const resData = response.data
    return resData
})

export const createCustomer = createAsyncThunk('createCustomer', async (data: any) => {
    const response = await axiosInstance.post('admin/createCustomer', data, {
        headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
    })
    const resData = response.data.message
    return resData
})

export const fetchCustomerDetail = createAsyncThunk("fetchCustomerDetail", async (data:any) => {
    const response = await axiosInstance.get(`admin/customer/${data}`, {
        headers: { 'Authorization': `Bearer ${JSON.parse(localStorage.getItem('userDetail') as string).accessToken}`}
    })
    const resData = response.data
    return resData
})

export const updateCustomer = createAsyncThunk('updateCustomer', async (data: any) => {
    const response = await axiosInstance.post('admin/updateCustomer', data, {
        headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
    })
    const resData = response.data.message
    return resData
})

export const fetchProcessList = createAsyncThunk('processList', async (data?: { searchText?: String, limit?: number, page?: number }) => {
    try {
        const response = await axiosInstance.get('admin/processList',
            {
                params: {
                    search: data?.searchText,
                    limit: data?.limit,
                    page: data?.page
                },
                headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
            })
        const resData = response.data
        return resData
    } catch (error) {
        return []
    }
})

export const createNewProcess = createAsyncThunk('createProcess', async (data: any) => {
    const response = await axiosInstance.post('admin/createProcess', data, {
        headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
    })
    const resData = response.data.message
    return resData
})

export const updateProcess = createAsyncThunk('updateProcess', async (data: any) => {
    const response = await axiosInstance.post('admin/updateProcess', data, {
        headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
    })
    const resData = response.data.message
    return resData
})

export const deleteProcess = createAsyncThunk('deleteProcess', async (data: any) => {
    const response = await axiosInstance.post('admin/deleteProcess', data, {
        headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
    })
    const resData = response.data.message
    return resData
})

export const fetchVendorHistory = createAsyncThunk('vendorHistory', async (data?: { searchText?: String, limit?: number, page?: number }) => {
    try {
        const response = await axiosInstance.get('admin/vendorsHistory',
            {
                params: {
                    search: data?.searchText,
                    limit: data?.limit,
                    page: data?.page
                },
                headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
            })
        const resData = response.data
        return resData
    } catch (error) {
        return []
    }
})

export const fetchSupplierHistory = createAsyncThunk('supplierHistory', async (data?: { searchText?: String, limit?: number, page?: number }) => {
    try {
        const response = await axiosInstance.get('admin/suppliersHistory',
            {
                params: {
                    search: data?.searchText,
                    limit: data?.limit,
                    page: data?.page
                },
                headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
            })
        const resData = response.data
        return resData
    } catch (error) {
        return []
    }
})

export const fetchSupplierBOs = createAsyncThunk('supplierBoughtouts', async (data?: { id: String, searchText?: String, 
    limit?: number, page?: number }) => {
    try {
        const response = await axiosInstance.get(`machine/supplierBoughtouts/${data?.id}`,
            {
                headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
            })
        const resData = response.data
        return resData
    } catch (error) {
        return []
    }
})

export const fetchCustomerHistory = createAsyncThunk('customerHistory', async (data?: { searchText?: String, limit?: number, page?: number }) => {
    try {
        const response = await axiosInstance.get('admin/customersHistory',
            {
                params: {
                    search: data?.searchText,
                    limit: data?.limit,
                    page: data?.page
                },
                headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
            })
        const resData = response.data
        return resData
    } catch (error) {
        return []
    }
})

export const fetchEnquiries = createAsyncThunk('enquiries', async (data?: { searchText?: String, limit?: number, page?: number, status?: string }) => {
    const response = await axiosInstance.get('admin/enquiries',
        {
            params: {
                search: data?.searchText,
                limit: data?.limit,
                page: data?.page,
                status: data?.status
            },
            headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
        })
    const resData = response.data
    return resData
})

export const createNewEnquiry = createAsyncThunk('createEnquiry', async (data: any) => {
    const response = await axiosInstance.post('admin/createEnquiry', data, {
        headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
    })
    const resData = response.data
    return resData
})

export const updateEnquiryStatus = createAsyncThunk('updateEnquiryStaus', async (data: any) => {
    const response = await axiosInstance.post('admin/updateEnquiryStatus', data, {
        headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
    })
    const resData = response.data.message
    return resData
})

export const fetchEmployeeAttendanceList = createAsyncThunk('employee_attendance', async (data?: { attendance_date: String }) => {
    const response = await axiosInstance.post('user/employee-daily-attendance', { attendance_date: data?.attendance_date }, {
        headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
    })
    const resData = response.data
    return resData
})

export const fetchLeaveRequestList = createAsyncThunk('leave_request', async (data?: any) => {
    const response = await axiosInstance.post('user/leave-request-list', data, {
        headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
    })
    const resData = response.data
    return resData
})

export const updateAttendanceStatus = createAsyncThunk('updateAttendanceStaus', async (data: any) => {
    const response = await axiosInstance.post('user/attendance-update', data, {
        headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
    })
    const resData = response.data.message
    return resData
})

export const updateLeaveStatus = createAsyncThunk('updateLeaveStatus', async (data: any) => {
    const response = await axiosInstance.post('user/update-leave-request', data, {
        headers: { 'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem("userDetail") as string).accessToken }
    })
    const resData = response.data.message
    return resData
})

const adminSlice = createSlice({
    name: 'admin',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchRoles.pending, (state) => {
                state.status = 'loading';
                state.error = null
            })
            .addCase(fetchRoles.fulfilled, (state, action) => {
                state.status = 'idle';
                state.roles = action.payload
            })
            .addCase(fetchRoles.rejected, (state, action) => {
                state.status = 'error';
                state.error = action.error.message || "Unable to load roles"
            })

            .addCase(fetchUsers.pending, (state) => {
                state.status = 'loading';
                state.error = null
            })
            .addCase(fetchUsers.fulfilled, (state, action) => {
                state.status = 'idle';
                state.users = action.payload
            })
            .addCase(fetchUsers.rejected, (state, action) => {
                state.status = 'error';
                state.error = action.error.message || "Unable to load users"
            })

            .addCase(fetchVendors.pending, (state) => {
                state.status = 'loading';
                state.error = null
            })
            .addCase(fetchVendors.fulfilled, (state, action) => {
                state.status = 'idle';
                state.vendors = action.payload
            })
            .addCase(fetchVendors.rejected, (state, action) => {
                state.status = 'error';
                state.error = action.error.message || "Unable to load vendors"
            })

            .addCase(fetchSuppliers.pending, (state) => {
                state.status = 'loading';
                state.error = null
            })
            .addCase(fetchSuppliers.fulfilled, (state, action) => {
                state.status = 'idle';
                state.suppliers = action.payload
            })
            .addCase(fetchSuppliers.rejected, (state, action) => {
                state.status = 'error';
            })

            .addCase(fetchCustomers.pending, (state) => {
                state.status = 'loading';
                state.error = null
            })
            .addCase(fetchCustomers.fulfilled, (state, action) => {
                state.status = 'idle'
                state.customers = action.payload
            })
            .addCase(fetchCustomers.rejected, (state, action) => {
                state.status = 'error';
                state.error = action.error.message || "Unable to load vendors"
            })

            .addCase(fetchProcessList.pending, (state) => {
                state.status = 'loading';
                state.error = null
            })
            .addCase(fetchProcessList.fulfilled, (state, action) => {
                state.status = 'idle';
                state.processList = action.payload
            })
            .addCase(fetchProcessList.rejected, (state, action) => {
                state.status = 'error';
                state.error = action.error.message || "Unable to load vendors"
            })

            .addCase(createNewUser.pending, (state) => {
                state.status = 'loading';
                state.error = null
            })
            .addCase(createNewUser.fulfilled, (state, action) => {
                state.status = 'idle';
                state.message = action.payload
            })
            .addCase(createNewUser.rejected, (state, action) => {
                state.status = 'error';
                state.error = action.error.message || "Unable to load vendors"
            })

            .addCase(deleteUser.pending, (state) => {
                state.status = 'loading';
                state.error = null
            })
            .addCase(deleteUser.fulfilled, (state, action) => {
                state.status = 'idle';
                state.message = action.payload
            })
            .addCase(deleteUser.rejected, (state, action) => {
                state.status = 'error';
                state.error = action.error.message || "Unable to delete user"
            })

            .addCase(createNewRole.pending, (state) => {
                state.status = 'loading';
                state.error = null
            })
            .addCase(createNewRole.fulfilled, (state, action) => {
                state.status = 'idle';
                state.message = action.payload
            })
            .addCase(createNewRole.rejected, (state, action) => {
                state.status = 'error';
                state.error = action.error.message || "Unable to load vendors"
            })

            .addCase(deleteRole.pending, (state) => {
                state.status = 'loading';
                state.error = null
            })
            .addCase(deleteRole.fulfilled, (state, action) => {
                state.status = 'idle';
                state.message = action.payload
            })
            .addCase(deleteRole.rejected, (state, action) => {
                state.status = 'error';
                state.error = action.error.message || "Unable to delete role"
            })

            .addCase(createNewProcess.pending, (state) => {
                state.status = 'loading';
                state.error = null
            })
            .addCase(createNewProcess.fulfilled, (state, action) => {
                state.status = 'idle';
                state.message = action.payload
            })
            .addCase(createNewProcess.rejected, (state, action) => {
                state.status = 'error';
                state.error = action.error.message || "Unable to load vendors"
            })

            .addCase(deleteProcess.pending, (state) => {
                state.status = 'loading';
                state.error = null
            })
            .addCase(deleteProcess.fulfilled, (state, action) => {
                state.status = 'idle';
                state.message = action.payload
            })
            .addCase(deleteProcess.rejected, (state, action) => {
                state.status = 'error';
                state.error = action.error.message || "Unable to delete process"
            })

            .addCase(createVendor.pending, (state) => {
                state.status = 'loading';
                state.error = null
            })
            .addCase(createVendor.fulfilled, (state, action) => {
                state.status = 'idle';
                state.message = action.payload
            })
            .addCase(createVendor.rejected, (state, action) => {
                state.status = 'error';
                state.error = action.error.message || "Unable to load vendors"
            })

            .addCase(deleteVendor.pending, (state) => {
                state.status = 'loading';
                state.error = null
            })
            .addCase(deleteVendor.fulfilled, (state, action) => {
                state.status = 'idle';
                state.message = action.payload
            })
            .addCase(deleteVendor.rejected, (state, action) => {
                state.status = 'error';
                state.error = action.error.message || "Unable to delete vendor"
            })

            .addCase(createSupplier.pending, (state) => {
                state.status = 'loading';
                state.error = null
            })
            .addCase(createSupplier.fulfilled, (state, action) => {
                state.status = 'idle';
                state.message = action.payload
            })
            .addCase(createSupplier.rejected, (state, action) => {
                state.status = 'error';
                state.error = action.error.message || "Unable to load vendors"
            })

            .addCase(deleteSupplier.pending, (state) => {
                state.status = 'loading';
                state.error = null
            })
            .addCase(deleteSupplier.fulfilled, (state, action) => {
                state.status = 'idle';
                state.message = action.payload
            })
            .addCase(deleteSupplier.rejected, (state, action) => {
                state.status = 'error';
                state.error = action.error.message || "Unable to delete supplier"
            })

            .addCase(createCustomer.pending, (state) => {
                state.status = 'loading';
                state.error = null
            })
            .addCase(createCustomer.fulfilled, (state, action) => {
                state.status = 'idle';
                state.message = action.payload
            })
            .addCase(createCustomer.rejected, (state, action) => {
                state.status = 'error';
                state.error = action.error.message || "Unable to load vendors"
            })

            .addCase(fetchEnquiries.pending, (state) => {
                state.status = 'loading';
                state.error = null
            })
            .addCase(fetchEnquiries.fulfilled, (state, action) => {
                state.status = 'idle';
                state.enquiries = action.payload
            })
            .addCase(fetchEnquiries.rejected, (state, action) => {
                state.status = 'error';
                state.error = action.error.message || "Unable to load enquiries"
            })
    }
})

export default adminSlice.reducer;
